
import { useState, useContext } from 'react';
import { MockGooglePlace, mockGooglePlaces } from '@/utils/mockGoogleMapsApi';
import { searchGooglePlaces, getPlaceDetails } from '@/utils/googlePlacesProxy';
// import { DataContext } from '@/context/DataContext'; // Removed
import { useToast } from '@/components/ui/use-toast';

export const useGoogleBusinessSearch = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // const { appData } = useContext(DataContext); // Removed - API key assumed to be handled by googlePlacesProxy/mapsScriptLoader
  const { toast } = useToast();

  // API key is no longer directly managed by this hook.
  // It's used by mapsScriptLoader, and googlePlacesProxy relies on the script being loaded.

  // Search for businesses using the Places API
  const searchBusinesses = async (query: string): Promise<MockGooglePlace[]> => {
    if (query.length < 2) return [];
    
    setIsLoading(true);
    setError(null);
    
    try {
      // API key check is implicitly handled by mapsScriptLoader & getPlacesService in proxy
      // if (!appData?.googleMapsApiKey) { // This check could be done here too for early exit
      //   console.warn('No Google Maps API key configured in appData for searchBusinesses');
      //   setError('Google Maps API key is not configured.');
      //   throw new Error('Google Maps API key is not configured.');
      // }
      
      const places = await searchGooglePlaces(query); // No longer pass apiKey
      
      if (places.length === 0) {
        // This is not necessarily an error, could be no results.
        // setError(`No businesses found for "${query}"`); 
        console.log(`No businesses found for "${query}"`);
      }
      
      return places;
    } catch (err) {
      console.error('Error searching businesses:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      
      // Toast for API key/CORS issues might be less relevant now if using JS API directly.
      // Errors from searchGooglePlaces (if it rejects) will be caught here.
      // The new searchGooglePlaces falls back to mock data on error, so this catch might not be hit often
      // unless searchGooglePlaces is changed to reject on API errors.
      
      // If searchGooglePlaces resolves with mock data, that's handled by `usingMockData` in useBusinessSearch.
      // If it throws (e.g. API not loaded), it will be caught here.
      
      // The original code had specific toasts for 403/CORS, which are less likely with JS API.
      // A generic error toast might be sufficient if searchGooglePlaces rejects.
      toast({
        title: "Search Error",
        description: errorMessage,
        variant: "destructive",
      });
      
      throw err; 
    } finally {
      setIsLoading(false);
    }
  };

  // Get details for a specific business by place ID
  const getBusinessDetails = async (placeId: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // API key check is implicit via mapsScriptLoader & getPlacesService in proxy
      // if (!appData?.googleMapsApiKey) {
      //   setError('Google Maps API key is not configured.');
      //   throw new Error('Google Maps API key is not configured.');
      // }
      
      const details = await getPlaceDetails(placeId); // No longer pass apiKey
      
      // Try to extract opening date if available in the place details
      // This logic might need adjustment based on the fields returned by JS API PlaceDetails
      if (details && details.opening_date) { 
        details.founded = details.opening_date;
      } else if (details && details.opening_year) {
        details.founded = details.opening_year.toString();
      }
      
      return details;
    } catch (err) {
      console.error('Error getting business details:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      
      // Show toast for error
      toast({
        title: "Failed to Get Business Details",
        description: errorMessage.includes('403') 
          ? "API key may be invalid or needs Places API enabled."
          : "Could not retrieve business details. Please try again.",
        variant: "destructive",
      });
      
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    searchBusinesses,
    getBusinessDetails,
    isLoading,
    error
  };
};
